<template>
  <section :class="setStyleClasses">
    <div v-if="composedTeaser.externalCssClass"
         :class="composedTeaser.externalCssClass" />
    <a v-else
       :href="composedTeaser.url"
       :rel="composedTeaser.rel"
       :target="setLinkTarget"
       :tabindex="setTabindex"
       data-tc="teaser"
       @mouseenter="teaserMouseState = 'enter'"
       @mouseleave="teaserMouseState = 'leave'">
      <figure v-if="imageUrl || videoMotionThumbnailEnabled"
              class="bx-teaser__image">
        <div v-if="composedTeaser.overlayImageUrl && composedTeaser.overlayImageUrl.length > 0"
             :class="getImageOverlayStyleClass(composedTeaser)" />
        <VideoMotionThumbnail v-if="videoMotionThumbnailEnabled"
                              :media-id="composedTeaser.videoMediaId"
                              :teaser-mouse-state="teaserMouseState"
                              :loading="getLoadingValue"
                              @video-motion-thumbnail-self-disabled="videoMotionThumbnailSelfDisabled = $event" />
        <picture v-if="imageUrl && !videoMotionThumbnailEnabled">
          <source v-for="(singleSrc, index) in imgSrcSetArray('webp')"
                  :key="index + 'webp'"
                  type="image/webp"
                  :loading="getLoadingValue"
                  :media="setMediaQuery(singleSrc.viewportWidth)"
                  :srcset="singleSrc.src">
          <img :src="fallbackPlaceholderSource"
               data-pin-nopin="true"
               :loading="getLoadingValue"
               width="1600"
               height="900"
               :alt="getImageAltText(composedTeaser, imageAltText)"
               :title="getImageAltText(composedTeaser, imageAltText)">
        </picture>
        <div v-if="(composedTeaser.advertorial && !composedTeaser.hideAdvertorial) || composedTeaser.affiliate || composedTeaser.ownerBrand"
             class="bx-badges bx-badges--top bx-badges--over-image">
          <span v-if="composedTeaser.ownerBrand"
                class="bx-badge bx-badge--brand">
            <img :src="brandBadgeSrc('icon')"
                 :alt="`${setBrandBadgeAlt} Icon`"
                 class="bx-badge--brand__icon">
            <img :src="brandBadgeSrc('logo')"
                 :alt="`${setBrandBadgeAlt} Logo`"
                 class="bx-badge--brand__logo">
          </span>
          <span v-if="(composedTeaser.advertorial && !composedTeaser.hideAdvertorial) || composedTeaser.affiliate"
                class="bx-badge bx-badge--ad"
                :class="{ 'bx-badge--affiliate-link': composedTeaser?.affiliate }" />
        </div>
        <div v-if="showPlayIcon || showDownloadIcon || showGalleryIcon"
             class="bx-badges bx-badges--bottom bx-badges--over-image">
          <svg v-if="showPlayIcon"
               class="bx-badge bx-badge--play icon icon-play">
            <use href="/images/icon_sprite.svg#play" />
          </svg>
          <svg v-if="showDownloadIcon"
               class="bx-badge bx-badge--download icon icon-download">
            <use href="/images/icon_sprite.svg#download" />
          </svg>
          <svg v-if="showGalleryIcon"
               class="bx-badge bx-badge--gallery icon icon-gallery">
            <use href="/images/icon_sprite.svg#gallery" />
          </svg>
        </div>

        <template v-if="isInlineTeaser && composedTeaser.hasSlider && composedTeaser.numSlides && composedTeaser.sliderContents">
          <div class="bx-badge--slider-arrow">
            <svg class="icon icon-arrow_slider_prev">
              <use href="/images/icon_sprite.svg#arrow_pager-prev" />
            </svg>
          </div>
          <div class="bx-badge--slider-arrow bx-badge--slider-arrow-next">
            <svg class="icon icon-arrow_slider_next">
              <use href="/images/icon_sprite.svg#arrow_pager-next" />
            </svg>
          </div>
          <span class="bx-badge--image-count">
            <svg class="icon icon-layers">
              <use href="/images/icon_sprite.svg#layers" />
            </svg>
            {{ composedTeaser.numSlides }} {{ composedTeaser.sliderContents }}
          </span>
        </template>
      </figure>
      <figure v-else-if="metaData.isSliderPreview"
              class="bx-teaser__image">
        <div v-if="composedTeaser.overlayImageUrl && composedTeaser.overlayImageUrl.length > 0"
             :class="getImageOverlayStyleClass(composedTeaser)" />
        <img v-if="metaData.isLoading"
             :src="metaData.imgUrlLoading">
        <img v-else-if="metaData.error"
             :src="metaData.imgUrlError">
        <img v-else-if="metaData.errorNotEnoughContent"
             :src="metaData.imgUrlErrorNotEnoughContent">
        <img v-else
             :src="'/images/default-placeholder.jpg'">
      </figure>

      <div class="bx-teaser__content"
           data-tc="teaser-content">
        <div v-if="showPlayIcon || showDownloadIcon || showGalleryIcon"
             class="bx-badges bx-badges--bottom bx-badges--over-content">
          <svg v-if="showPlayIcon"
               class="bx-badge bx-badge--play icon icon-play">
            <use href="/images/icon_sprite.svg#play" />
          </svg>
          <svg v-if="showDownloadIcon"
               class="bx-badge bx-badge--download bx-badge--over-content icon icon-download">
            <use href="/images/icon_sprite.svg#download" />
          </svg>
          <svg v-if="showGalleryIcon"
               class="bx-badge bx-badge--gallery bx-badge--over-content icon icon-gallery">
            <use href="/images/icon_sprite.svg#gallery" />
          </svg>
        </div>
        <div class="bx-badges bx-badges--top bx-badges--over-content">
          <span v-if="(composedTeaser.advertorial && !composedTeaser.hideAdvertorial) || composedTeaser.affiliate"
                class="bx-badge bx-badge--ad"
                :class="{ 'bx-badge--affiliate-link': composedTeaser?.affiliate }" />
          <span v-if="composedTeaser.ownerBrand"
                class="bx-badge bx-badge--brand">
            <img :src="`/images/${composedTeaser.ownerBrand}/icon-badge.png`"
                 :alt="`${setBrandBadgeAlt} Icon`"
                 class="bx-badge--brand__icon">
            <img :src="`/images/${composedTeaser.ownerBrand}/logo-badge.png`"
                 :alt="`${setBrandBadgeAlt} Logo`"
                 class="bx-badge--brand__logo">
          </span>
        </div>

        <div v-if="composedTeaser.roofline"
             class="bx-teaser__roofline"
             :data-nosnippet="shouldAddDataNoSnippet"
             data-tc="teaser-roofline">
          {{ composedTeaser.roofline }}
        </div>

        <component :is="headlineTagType"
                   v-if="headline"
                   :data-nosnippet="shouldAddDataNoSnippet"
                   class="bx-teaser__headline"
                   data-tc="teaser-headline">
          {{ headline }}
        </component>

        <p v-if="copytext"
           :data-nosnippet="shouldAddDataNoSnippet"
           class="bx-teaser__copy">
          {{ copytext }}
        </p>

        <div v-if="showPlayIcon"
             class="bx-badges bx-badges--below-teaser-content">
          <svg class="bx-badge bx-badge--video icon icon-play">
            <use href="/images/icon_sprite.svg#play" />
          </svg>
        </div>
      </div>
      <TrackingPixel v-if="composedTeaser.trackingPixel"
                     :tracking-pixel="composedTeaser.trackingPixel" />
    </a>
  </section>
</template>

<script>
import sharedTeaser from '../../mixins/shared-teaser'

export default {
  mixins: [sharedTeaser]
}
</script>
